import React, { useEffect, useRef, useState } from "react";
import style from "./DataQuality.module.scss";
// import DoughnutChart from "./DoughnutChart";
import { Accordion, Card } from "react-bootstrap";
import {
  getDashboardDataQualityHistory,
  getDmDataQuality,
  getRdvDataQuality,
} from "../../services/Dashboard.service";
import { IDQHistoryChart } from "../../interfaces/dashboard.interfaces";
import LineChart from "./LineChart";
import refreshIcon from "../../components/Icons/refresh.svg";
import CustomLoader from "../../components/Loader/Spinner";
import DataQualityTable from "./DataQualityTable";

const DataQuality = ({ setDqMonitorTab }: any) => {
  const chartPalette = [
    "#22D3EE",
    "#38BDF8",
    "#2DD4BF",
    "#34D399",
    "#60A5FA",
    "#818CF8",
    "#A78BFA",
    "#F472B6",
    "#F59E0B",
    "#F97316",
  ];
  const [dataVaultData, setDataVaultData] = useState();
  const [dataMartData, setDataMartData] = useState();
  const [dataVaultPage, setDataVaultPage] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState(tableData);
  const [chartData, setChartData] = useState([]);
  const [isChartSelected, setIsChartSelected] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [rdvChartData, setRdvChartData] = useState<any>();
  const [dataMartChartData, setDataMartChartData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [rdvHistoryData, setRdvHistoryData] = useState<IDQHistoryChart>();
  const [dmHistoryData, setDmHistoryData] = useState<IDQHistoryChart>();
  const chartRef = useRef(null);

  const fetchSilverDataQualityData = () => {
    getRdvDataQuality().then((res: any) => {
      res.sort((a: any, b: any) => {
        const nameA = a.data_vault_ensemble.toUpperCase();
        const nameB = b.data_vault_ensemble.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      setDataVaultData(res);
      loadDataVaultPage(res);
      setIsLoading(false);
    });
  };

  const fetchGoldDataQualityData = () => {
    getDmDataQuality().then((res: any) => {
      const modifiedArray = res.map((obj: any) => {
        const { data_mart_name, ...rest } = obj;
        return { data_mart: data_mart_name, ...rest };
      });
      setDataMartData(modifiedArray);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchSilverDataQualityData();
    fetchGoldDataQualityData();
    getDashboardDataQualityHistory().then((res) => {
      setRdvHistoryData(res.raw_data_vault);
      setDmHistoryData(res.data_mart);
      getRdvChartData(res.raw_data_vault);
      getDataMartChartData(res.data_mart);
    });
  }, []);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (isChartSelected === true) {
        if (chartRef.current && chartRef.current.contains(event.target)) {
          setIsChartSelected(true);
        } else {
          const filtered = tableData.filter((item) =>
            Object.keys(filterOptions)?.every((property) => {
              if (filterOptions[property].size === 0) return true;
              return filterOptions[property].has(item[property]);
            })
          );
          setFilteredTableData(filtered);
          calculatePercentageOfStatus(filtered);
          setIsChartSelected(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChartSelected]);

  useEffect(() => {
    const filtered = tableData.filter((item) =>
      Object.keys(filterOptions)?.every((property) => {
        if (filterOptions[property].size === 0) return true;
        return filterOptions[property].has(item[property]);
      })
    );

    // The trend chart follows only the subject area / data mart filter.
    const selectedSeries = (
      history: IDQHistoryChart | undefined,
      property: string
    ) =>
      history && {
        ...history,
        series: history.series.filter(
          (series) =>
            !filterOptions[property] ||
            filterOptions[property].size === 0 ||
            filterOptions[property].has(series.name)
        ),
      };

    if (!isChartSelected) {
      setFilteredTableData(filtered);
      calculatePercentageOfStatus(filtered);
      if (dataVaultPage) {
        getRdvChartData(selectedSeries(rdvHistoryData, "subject_area"));
      } else {
        getDataMartChartData(selectedSeries(dmHistoryData, "data_mart"));
      }
    }
  }, [filterOptions, tableData]);

  // One line per subject area / data mart: failed test cases per day, already
  // aggregated by the API.
  const historyChartData = (history: IDQHistoryChart) => ({
    labels: history.dates,
    datasets: history.series.map((series, index) => {
      const color = chartPalette[index % chartPalette.length];
      return {
        label: series.name,
        data: series.failed_test_cases,
        borderColor: color,
        backgroundColor: color,
        tension: 0,
        borderDash: [0],
      };
    }),
  });

  const getRdvChartData = (history?: IDQHistoryChart) => {
    if (history) setRdvChartData(historyChartData(history));
  };

  const getDataMartChartData = (history?: IDQHistoryChart) => {
    if (history) setDataMartChartData(historyChartData(history));
  };

  const loadDataVaultPage = (res: any) => {
    setTableData(res);
    setFilteredTableData(res);
    calculatePercentageOfStatus(res);
    getFilterDataForDataVault(res);
  };

  const loadDataMartPage = (res: any) => {
    res.sort((a: any, b: any) => {
      const nameA = a?.data_mart.toUpperCase();
      const nameB = b?.data_mart.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });

    setTableData(res);
    setFilteredTableData(res);
    calculatePercentageOfStatus(res);
    getFilterDataForDataMart(res);
  };

  const calculatePercentageOfStatus = (data: any) => {
    const jobGroups = new Map();
    data.forEach((item: any) => {
      const data_vault_ensemble = item.data_vault_ensemble
        ? item.data_vault_ensemble
        : item.data_mart_table_1;
      const data_vault_test_passed = item.data_vault_test_passed
        ? item.data_vault_test_passed
        : item.data_mart_test_passed;

      if (!jobGroups.has(data_vault_ensemble)) {
        jobGroups.set(data_vault_ensemble, { total: 0, passed: 0, failed: 0 });
      }

      const group = jobGroups.get(data_vault_ensemble);
      group.total++;

      if (data_vault_test_passed === "PASSED") {
        group.passed++;
      } else if (data_vault_test_passed === "FAILED") {
        group.failed++;
      }
    });

    const jobPercentages: any = [];

    jobGroups.forEach((group, data_vault_ensemble) => {
      const { passed, failed } = group;
      const passedPercentage = passed;
      const failedPercentage = failed;

      jobPercentages.push({
        chartType: data_vault_ensemble,
        passedPercentage,
        failedPercentage,
      });
    });

    setChartData(jobPercentages);
  };

  const getFilterDataForDataVault = (data: any) => {
    const options: any = {};
    data.forEach((item: any) => {
      Object.keys(item).forEach((key) => {
        // if (!options[key]) {
        //   options[key] = new Set();
        // }
        // options[key].add(item[key].toString());
        if (!options[key]) {
          options[key] = new Set();
        }
        // Check if item[key] is null or undefined before calling toString()
        if (item[key] !== null && item[key] !== undefined) {
          options[key].add(item[key].toString());
        } else {
          // Add a default string (or you could choose to skip it)
          options[key].add("null");
        }
      });
    });

    Object.keys(options).forEach((key) => {
      options[key] = Array.from(options[key]);
    });

    const requiredFilter: any = [
      {
        id: 1,
        filterName: "data_vault_test_passed",
        options: options.data_vault_test_passed.map(
          (item: any, index: any) => ({
            id: index,
            name: item,
            selected: false,
          })
        ),
      },
      {
        id: 2,
        filterName: "subject_area",
        options: options.subject_area.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
      {
        id: 3,
        filterName: "data_vault_ensemble",
        options: options.data_vault_ensemble.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
      {
        id: 4,
        filterName: "data_vault_test",
        options: options.data_vault_test.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
      {
        id: 5,
        filterName: "md_system_id",
        options: options.md_system_id.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      }
    ];

    setFilterData(requiredFilter);
  };

  const getFilterDataForDataMart = (data: any) => {
    const options: any = {};
    data.forEach((item: any) => {
      Object.keys(item).forEach((key) => {
        if (!options[key]) {
          options[key] = new Set();
        }
        options[key].add(item[key]?.toString());
      });
    });

    Object.keys(options).forEach((key) => {
      options[key] = Array.from(options[key]);
    });

    const requiredFilter: any = [
      {
        id: 1,
        filterName: "data_mart_test_passed",
        options: options?.data_mart_test_passed?.map(
          (item: any, index: any) => ({
            id: index,
            name: item,
            selected: false,
          })
        ),
      },
      {
        id: 2,
        filterName: "data_mart",
        options: options?.data_mart?.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
      {
        id: 3,
        filterName: "data_mart_test",
        options: options.data_mart_test?.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
      {
        id: 4,
        filterName: "data_mart_table_1",
        options: options.data_mart_table_1?.map((item: any, index: any) => ({
          id: index,
          name: item,
          selected: false,
        })),
      },
    ];

    setFilterData(requiredFilter);
  };

  const handleFilterItemSelect = (
    filterId: number,
    itemId: number,
    filterName: string,
    itemName: string,
    checked: boolean
  ) => {
    const updatedFilteredData = filterData.map((value: any) => {
      if (filterId === value.id) {
        const newOptions = value.options.map((item: any) => {
          if (item.id === itemId) {
            return { ...item, selected: checked };
          } else {
            return item;
          }
        });
        return { ...value, options: newOptions };
      } else {
        return value;
      }
    });
    setFilterData(updatedFilteredData);

    const newOptions: any = new Set(filterOptions[filterName]);
    if (newOptions.has(itemName)) {
      newOptions.delete(itemName);
    } else {
      newOptions.add(itemName);
    }

    setFilterOptions((prevOptions: any) => ({
      ...prevOptions,
      [filterName]: newOptions,
    }));
  };

  const handleFilterSelect = (
    filterId: number,
    filterName: string,
    checked: boolean
  ) => {
    const updatedFilteredData = filterData.map((value: any) => {
      if (filterId === value.id) {
        const newOptions = value.options.map((item: any) => {
          return { ...item, selected: checked };
        });
        return { ...value, options: newOptions };
      } else {
        return value;
      }
    });
    setFilterData(updatedFilteredData);
    const newOptions: any = new Set(filterOptions[filterName]);

    filterData.forEach((value: any) => {
      if (filterId === value.id) {
        value.options.forEach((item: any) => {
          checked && newOptions.add(item.name);
          !checked && newOptions.delete(item.name);
        });
      }
    });

    setFilterOptions((prevOptions: any) => ({
      ...prevOptions,
      [filterName]: newOptions,
    }));
  };

  const clearFilter = () => {
    const updatedFilterData = filterData.map((item: any) => ({
      ...item,
      options: item.options.map((option: any) => ({
        ...option,
        selected: false,
      })),
    }));
    setFilterData(updatedFilterData);
    setFilterOptions({});
  };

  const handleSilverDqDataRefresh = () => {
    setIsLoading(true);
    fetchSilverDataQualityData();
  };

  const handleGoldDqDataRefresh = () => {
    setIsLoading(true);
    fetchGoldDataQualityData();
  };

  return (
    <>
      <div
        className={style.dataQualityContainer}
      >
        <div
          className={style.dataQualityContainerHeader}
        >
          <div className={style.tabSwitchRail}>
            <button
              type="button"
              className={`${style.jobDetailsContainerHeader} ${
                dataVaultPage === true ? style.selectedJobContainer : ""
              }`}
              aria-pressed={dataVaultPage === true}
              onClick={() => {
                setDataVaultPage(true);
                loadDataVaultPage(dataVaultData);
                setFilterOptions({});
              }}
            >
              Silver DQ History Status
            </button>
            <button
              type="button"
              className={`${style.jobTaskContainerHeader} ${
                dataVaultPage === false ? style.selectedJobContainer : ""
              }`}
              aria-pressed={dataVaultPage === false}
              onClick={() => {
                setDataVaultPage(false);
                loadDataMartPage(dataMartData);
                setFilterOptions({});
              }}
            >
              Gold DQ History Status
            </button>
          </div>
          <div className={style.backButton}>
            <button
              type="button"
              onClick={() => setDqMonitorTab(false)}
              className={style.dashboardLink}
            >
              Switch to Dashboard View
            </button>
          </div>
        </div>
        <div className={style.dataQualityContentContainer}>
          <div className={style.chartAndFilterBox}>
            <div className={style.chartBox}>
              <div className={style.chartBoxHeader}>
                <div className={style.panelTitleGroup}>
                  <p className={style.panelEyebrow}>Overview</p>
                  <div className={style.orchestrationHeaderHeading}>
                    {dataVaultPage ? "Data Vault History Status" : "Data Mart History Status"}
                  </div>
                </div>
                <button
                  type="button"
                  className={style.refreshButton}
                  onClick={
                    dataVaultPage
                      ? handleSilverDqDataRefresh
                      : handleGoldDqDataRefresh
                  }
                >
                  <img
                    alt="Refresh data"
                    src={refreshIcon}
                    width={16}
                    className={style.refreshIcon}
                  />
                  <span className={style.refreshButtonLabel}>Refresh</span>
                </button>
              </div>
              <div className={style.chartBoxContent}>
                {/* <div className={style.chartBoxFirstRow}>
                  {isLoading && <CustomLoader variant="blue" />}
                    {!isLoading &&
                      chartData.map((value: any) => {
                        return (
                          <div className={style.dataQualityChartBox}>
                            <DoughnutChart
                              chartData={chartData}
                              type={value.chartType}
                              label={["Failed", "Succeeded"]}
                              setFilteredTableData={setFilteredTableData}
                              tableData={tableData}
                              ref={chartRef}
                              setIsChartSelected={setIsChartSelected}
                              setFilterData={setFilterData}
                              filterData={filterData}
                              setFilterOptions={setFilterOptions}
                              dataVaultPage={dataVaultPage}
                            />
                           
                          </div>
                        );
                    })
                  }
                </div> */}
                <div className={style.chartBoxFirstRow}>
                  {isLoading ? (
                    <CustomLoader variant="blue" />
                  ) : (
                    <DataQualityTable
                      chartData={chartData}
                      tableData={filteredTableData}
                      setFilteredTableData={setFilteredTableData}
                      setIsChartSelected={setIsChartSelected}
                      dataVaultPage={dataVaultPage}
                      TypeHeading = {dataVaultPage ? "Data Vault Ensemble" : "Data Mart Entity"}
                    />
                  )}
                </div>
                <div className={style.chartBoxSecondRow}>
                  <div className={style.dataQualityChartBox}>
                    <div className={style.dataQualityStatus}>
                      <div className={style.dataQualityStatusItem}>
                        <div
                          className={style.dataQualityStatusColorGreen}
                        ></div>
                        Succeeded
                      </div>
                      <div className={style.dataQualityStatusItem}>
                        <div className={style.dataQualityStatusColorRed}></div>
                        Failed
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={style.filterBox}>
              <div className={style.filterBoxHeader}>
                <div className={style.panelTitleGroup}>
                  <p className={style.panelEyebrow}>Controls</p>
                  <div className={style.sectionHeading}>Filter</div>
                </div>
                <button type="button" className={style.clearFilter} onClick={clearFilter}>
                  Clear Filter
                </button>
              </div>
              <div className={style.accordionContainer}>
                <div className={style.accordion}>
                  {filterData.map((item) => (
                    <div className={style.customAccordionContainer} key={item.filterName}>
                      <Accordion className={style.customAccordion}>
                        <Card className={style.customCard}>
                          <Accordion.Item eventKey="0">
                            <Accordion.Header
                              className={style.accordionItemHeader}
                            >
                              <label className={style.checkboxContainer}>
                                <input
                                  type="checkbox"
                                  name="selectedOption"
                                  checked={item.options?.every(
                                    (option: any) => option.selected
                                  )}
                                  onChange={(e) => {
                                    handleFilterSelect(
                                      item.id,
                                      item.filterName,
                                      e.target.checked
                                    );
                                  }}
                                />
                                <span className={style.checkmark}></span>
                                <span className={style.checkmarkLable}>
                                  {item.filterName}
                                </span>
                              </label>
                            </Accordion.Header>

                            <Accordion.Body className={style.accordionBody}>
                              {item.options?.map((option: any) => (
                                <div
                                  key={option.id}
                                  className={
                                    style.accordionBodyElementsContainer
                                  }
                                >
                                  <label className={style.checkboxContainer}>
                                    <input
                                      type="checkbox"
                                      name="selectedOption"
                                      checked={option.selected}
                                      onChange={(e) => {
                                        handleFilterItemSelect(
                                          item.id,
                                          option.id,
                                          item.filterName,
                                          option.name,
                                          e.target.checked
                                        );
                                      }}
                                    />
                                    <span className={style.checkmark}></span>
                                    <span className={style.checkmarkLable}>
                                      {option.name}
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </Accordion.Body>
                          </Accordion.Item>
                        </Card>
                      </Accordion>
                      <div className={style.accordionLine}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
         
          <div className={style.tableBox}>
            <div className={style.tableBoxHeader}>
              <div className={style.panelTitleGroup}>
                <p className={style.panelEyebrow}>Detailed View</p>
                <div className={style.sectionHeading}>
                  {dataVaultPage ? "Data Vault DQ Tests" : "Data Mart DQ Tests"}
                </div>
              </div>
            </div>
            {dataVaultPage && (
              <div
                className={`${style.jobManagementMainContainer} table-responsive`}
              >
                {isLoading && <CustomLoader variant="blue" />}
                {!isLoading && (
                  <table className={`${style.jobManagementTableStrip}`}>
                    <thead>
                      <tr className={style.jobManagementTableHead}>
                        <th className={`align-middle `}>Subject Area</th>
                        <th className={`align-middle `}>Data Vault Ensemble</th>
                        <th className={`align-middle ${style.wideColumn}`}>
                          DQ Test
                        </th>
                        <th className={`align-middle `}>Table Type</th>
                        <th className={`align-middle `}>Data Vault Table 1</th>
                        <th className={`align-middle `}>Data Vault Table 2</th>
                        <th className={`align-middle `}>DQ Test Passed</th>
                        <th className={`align-middle `}>Row Count</th>
                        <th className={`align-middle `}>Row Count Distinct</th>
                        <th className={`align-middle `}>System Id</th>
                        <th className={`align-middle ${style.wideColumn}`}>
                          Created at
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData.map((data) => (
                        <tr
                          className={style.jobManagementTableRow}
                          key={data.raw_data_vault_test_id}
                        >
                          <td className="align-middle">{data.subject_area}</td>
                          <td className="align-middle">
                            {data.data_vault_ensemble}
                          </td>
                          <td className="align-middle">
                            {data.data_vault_test}
                          </td>
                          <td className="align-middle">
                            {data.data_vault_type}
                          </td>
                          <td className={`align-middle `}>
                            {data.data_vault_table_1}
                          </td>
                          <td className={`align-middle `}>
                            {data.data_vault_table_2}
                          </td>
                          <td className={`align-middle `}>
                            <span
                              className={`${style.statusPill} ${
                                data.data_vault_test_passed === "PASSED"
                                  ? style.statusPassed
                                  : data.data_vault_test_passed === "FAILED"
                                  ? style.statusFailed
                                  : style.statusUnknown
                              }`}
                            >
                              {data.data_vault_test_passed}
                            </span>
                          </td>
                          <td className={`align-middle `}>{data.row_count}</td>
                          <td className={`align-middle `}>
                            {data.row_count_distinct}
                          </td>
                          <td className={`align-middle `}>
                            {data.md_system_id}
                          </td>
                          <td className={`align-middle ${style.dateCell}`}>
                            {data.created_at.substring(0, 19)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {!dataVaultPage && (
              <div
                className={`${style.jobManagementMainContainer} table-responsive`}
              >
                {isLoading && <CustomLoader variant="blue" />}
                {!isLoading && (
                  <table className={`${style.jobManagementTableStrip}`}>
                    <thead>
                      <tr className={style.jobManagementTableHead}>
                        <th className={`align-middle `}>Data Mart</th>
                        <th className={`align-middle ${style.wideColumn}`}>
                          DQ Test
                        </th>
                        <th className={`align-middle `}>Table Type</th>
                        <th className={`align-middle `}>Data Mart Table 1</th>
                        <th className={`align-middle `}>Data Mart Table 2</th>
                        <th className={`align-middle `}>DQ Test Passed</th>
                        <th className={`align-middle `}>Row Count</th>
                        <th className={`align-middle `}>Row Count Distinct</th>
                        <th className={`align-middle `}>Table Construct</th>
                        <th className={`align-middle ${style.wideColumn}`}>
                          Created at
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData.map((data) => (
                        <tr
                          className={style.jobManagementTableRow}
                          key={data.data_mart_test_id}
                        >
                          <td className="align-middle">{data.data_mart}</td>
                          <td className="align-middle">
                            {data.data_mart_test}
                          </td>
                          <td className="align-middle">
                            {data.data_mart_tbl_type}
                          </td>
                          <td className="align-middle">
                            {data.data_mart_table_1}
                          </td>
                          <td className={`align-middle `}>
                            {data.data_mart_table_2}
                          </td>
                          <td className={`align-middle `}>
                            <span
                              className={`${style.statusPill} ${
                                data.data_mart_test_passed === "PASSED"
                                  ? style.statusPassed
                                  : data.data_mart_test_passed === "FAILED"
                                  ? style.statusFailed
                                  : style.statusUnknown
                              }`}
                            >
                              {data.data_mart_test_passed}
                            </span>
                          </td>
                          <td className={`align-middle `}>{data.row_count}</td>
                          <td className={`align-middle `}>
                            {data.row_count_distinct}
                          </td>
                          <td className={`align-middle `}>
                            {data.data_mart_construct}
                          </td>
                          <td className={`align-middle ${style.dateCell}`}>
                            {data.created_at.substring(0, 19)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          <div className={style.graphBox}>
              <div className={style.graphBoxHeader}>
                <div className={style.panelTitleGroup}>
                  <p className={style.panelEyebrow}>Trend Analysis</p>
                  <div className={style.sectionHeading}>
                    {dataVaultPage ? "Data Vault Tests Over Time" : "Data Mart Tests Over Time"}
                  </div>
                </div>
              </div>
              <div className={style.graphContainer}>
                {rdvChartData && (
                  <LineChart
                    data={dataVaultPage ? rdvChartData : dataMartChartData}
                    type="dataQuality"
                    page={dataVaultPage}
                  />
                )}
              </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default DataQuality;
